import { Container } from '@playcanvas/pcui';
import { Vec3 } from 'playcanvas';

import { Events } from '../events';
import { Scene } from '../scene';
import {
    ANNOTATION_PRIORITIES,
    AnnotationPriority,
    buildAnnotationPriorityReadonly,
    buildAnnotationPrioritySelect,
    normalizeAnnotationPriority,
} from '../ui/annotation-priority';
import {
    BoundaryPlane,
    centroidVectors,
    computeBestFitPlane,
    computeStockpileVolume,
    polygonAreaXZ,
} from './boundary-volume-compute';
import { pointerPositionInElement } from './pointer-coordinates';

export type BoundaryRecord = {
    id: string;
    kind: 'boundary';
    title: string;
    vertices: [number, number, number][];
    position: [number, number, number];
    volumeM3: number | null;
    areaM2: number | null;
    basePlane: BoundaryPlane | null;
    computedAt: number | null;
    text: string;
    imageUrl: string;
    status: AnnotationPriority;
    tags: string[];
    cameraView: null;
};

const WHITE = '#ffffff';
const HIT_PX = 14;
const VERTEX_HIT_PX = 18;

function genId(): string {
    return `bnd_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`;
}

function hexToRgba(hex: string, alpha: number): string {
    const h = hex.replace('#', '');
    if (h.length < 6) return `rgba(255, 212, 59, ${alpha})`;
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${alpha})`;
}

function colorsForPriority(priority: AnnotationPriority, hover: boolean, selected: boolean) {
    const id = normalizeAnnotationPriority(priority);
    const entry = ANNOTATION_PRIORITIES.find((p) => p.id === id) ?? ANNOTATION_PRIORITIES.find((p) => p.id === 'low')!;
    const stroke = entry.color;
    const fillAlpha = hover || selected ? 0.24 : 0.14;
    return { stroke, fill: hexToRgba(stroke, fillAlpha) };
}

function distPointSeg2D(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
    const abx = bx - ax;
    const aby = by - ay;
    const apx = px - ax;
    const apy = py - ay;
    const ab2 = abx * abx + aby * aby;
    if (ab2 < 1e-12) return Math.hypot(apx, apy);
    let t = (apx * abx + apy * aby) / ab2;
    t = Math.max(0, Math.min(1, t));
    const qx = ax + abx * t;
    const qy = ay + aby * t;
    return Math.hypot(px - qx, py - qy);
}

function parseImportedBoundary(raw: Record<string, unknown>): BoundaryRecord | null {
    if (raw.kind !== 'boundary') return null;
    const id = typeof raw.id === 'string' ? raw.id : genId();
    const vertsRaw = raw.vertices;
    const verts: [number, number, number][] = [];
    if (Array.isArray(vertsRaw)) {
        for (const v of vertsRaw) {
            if (Array.isArray(v) && v.length >= 3) {
                verts.push([Number(v[0]) || 0, Number(v[1]) || 0, Number(v[2]) || 0]);
            } else if (v && typeof v === 'object') {
                const o = v as Record<string, number>;
                verts.push([Number(o.x) || 0, Number(o.y) || 0, Number(o.z) || 0]);
            }
        }
    }
    if (verts.length < 3) return null;
    const cv = centroidVectors(verts.map((t) => new Vec3(t[0], t[1], t[2])));
    const bpRaw = raw.basePlane;
    let basePlane: BoundaryPlane | null = null;
    if (bpRaw && typeof bpRaw === 'object') {
        const b = bpRaw as Record<string, number>;
        if (
            typeof b.nx === 'number' &&
            typeof b.ny === 'number' &&
            typeof b.nz === 'number' &&
            typeof b.d === 'number'
        ) {
            basePlane = { nx: b.nx, ny: b.ny, nz: b.nz, d: b.d };
        }
    }
    const legacyStatus = raw.status === 'open' ? 'low' : raw.status;
    return {
        id,
        kind: 'boundary',
        title: typeof raw.title === 'string' ? raw.title : 'Polygon',
        vertices: verts,
        position: [cv.x, cv.y, cv.z],
        volumeM3: typeof raw.volumeM3 === 'number' && Number.isFinite(raw.volumeM3) ? raw.volumeM3 : null,
        areaM2: typeof raw.areaM2 === 'number' && Number.isFinite(raw.areaM2) ? raw.areaM2 : null,
        basePlane,
        computedAt: typeof raw.computedAt === 'number' ? raw.computedAt : null,
        text: typeof raw.text === 'string' ? raw.text : '',
        imageUrl: typeof raw.imageUrl === 'string' ? raw.imageUrl : '',
        status: normalizeAnnotationPriority(legacyStatus ?? 'low'),
        tags: Array.isArray(raw.tags) ? (raw.tags as string[]) : [],
        cameraView: null,
    };
}

class BoundaryPolygonTool {
    activate: () => void;
    deactivate: () => void;

    constructor(events: Events, scene: Scene, parent: HTMLElement, canvasContainer: Container) {
        const records: BoundaryRecord[] = [];
        let selectedId: string | null = null;
        let hoverPolyId: string | null = null;
        const draft: Vec3[] = [];
        let cursorGhost: Vec3 | null = null;
        let active = false;
        let isEditMode = false;
        let debounceTimer: number | null = null;
        let dragVertex: { id: string; idx: number } | null = null;
        let clickGate = false;
        let pendingSelectOnly = false;

        const overlayHost = document.createElement('div');
        overlayHost.className = 'boundary-polygon-overlay';
        overlayHost.style.cssText =
            'position:absolute;left:0;top:0;width:100%;height:100%;pointer-events:none;z-index:34;overflow:visible;';
        canvasContainer.dom.appendChild(overlayHost);

        const cardHost = document.createElement('div');
        cardHost.className = 'boundary-polygon-card-host';
        cardHost.style.cssText =
            'position:absolute;left:0;top:0;width:100%;height:100%;pointer-events:none;z-index:36;overflow:visible;';
        canvasContainer.dom.appendChild(cardHost);

        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.classList.add('tool-svg', 'boundary-polygon-svg');
        svg.id = 'boundary-polygon-svg';
        overlayHost.appendChild(svg);

        let detailCardEl: HTMLDivElement | null = null;
        let detailCardRecId: string | null = null;

        const isIdleInteraction = (): boolean => {
            const t = events.invoke('tool.active') as string | null | undefined;
            return t == null || t === '';
        };

        const canEditVertices = (): boolean => active || (isEditMode && !!selectedId);

        const bumpDirty = () => {
            events.fire('annotation.requestBumpDirty');
        };

        const fireUpsert = (rec: BoundaryRecord) => {
            bumpDirty();
            events.fire('volugraph.rfi', { action: 'upsert', record: { ...rec } });
            events.fire('boundary.changed');
        };

        const fireDeleteId = (id: string) => {
            bumpDirty();
            events.fire('volugraph.rfi', { action: 'delete', id });
            events.fire('boundary.changed');
        };

        const selectedRec = (): BoundaryRecord | null =>
            selectedId ? records.find((r) => r.id === selectedId) ?? null : null;

        const displayIndexForId = (id: string) => {
            const i = records.findIndex((x) => x.id === id);
            return i >= 0 ? i + 1 : 1;
        };

        const worldVertsVec = (rec: BoundaryRecord): Vec3[] =>
            rec.vertices.map((t) => new Vec3(t[0], t[1], t[2]));

        const recalcCentroid = (rec: BoundaryRecord) => {
            const cv = centroidVectors(worldVertsVec(rec));
            rec.position = [cv.x, cv.y, cv.z];
        };

        const recomputeVolume = (rec: BoundaryRecord) => {
            const wv = worldVertsVec(rec);
            if (wv.length < 3) return;
            const plane = computeBestFitPlane(wv);
            rec.areaM2 = polygonAreaXZ(wv);
            rec.volumeM3 = computeStockpileVolume(scene, wv, plane);
            rec.basePlane = plane;
            rec.computedAt = Date.now();
            recalcCentroid(rec);
        };

        const scheduleRecompute = (rec: BoundaryRecord) => {
            if (debounceTimer) clearTimeout(debounceTimer);
            debounceTimer = window.setTimeout(() => {
                debounceTimer = null;
                recomputeVolume(rec);
                fireUpsert(rec);
                paint();
                if (detailCardRecId === rec.id) {
                    positionDetailCard(rec.id);
                }
            }, 250);
        };

        const ensureClosedLoopWorld = (poly: Vec3[]): Vec3[] => {
            if (poly.length < 2) return poly.slice();
            const out = poly.slice();
            const f = out[0];
            const l = out[out.length - 1];
            if (f.distance(l) > 1e-5) out.push(new Vec3(f.x, f.y, f.z));
            return out;
        };

        const _scrVec = new Vec3();

        const projectToScreen = (w: Vec3, out: { x: number; y: number; ok: boolean }) => {
            try {
                const cw = scene.canvas.clientWidth;
                const ch = scene.canvas.clientHeight;
                scene.camera.worldToScreen(w, _scrVec);
                if (_scrVec.z <= 0 || !Number.isFinite(_scrVec.x) || !Number.isFinite(_scrVec.y)) {
                    out.ok = false;
                    return;
                }
                out.x = _scrVec.x * cw;
                out.y = _scrVec.y * ch;
                out.ok = true;
            } catch {
                out.ok = false;
            }
        };

        const scrA = { x: 0, y: 0, ok: false };
        const scrB = { x: 0, y: 0, ok: false };

        const paint = () => {
            while (svg.firstChild) svg.removeChild(svg.firstChild);
            const { clientWidth: W, clientHeight: H } = canvasContainer.dom;
            if (!W || !H) return;
            svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
            svg.setAttribute('width', String(W));
            svg.setAttribute('height', String(H));

            const drawLoop = (
                poly: Vec3[],
                opts: { dashed: boolean; closed: boolean; fill: string | null; stroke: string; hover: boolean },
            ) => {
                const pv = opts.closed ? ensureClosedLoopWorld(poly) : poly;
                const pts: string[] = [];
                for (const p of pv) {
                    projectToScreen(p, scrA);
                    if (!scrA.ok) return;
                    pts.push(`${scrA.x.toFixed(2)},${scrA.y.toFixed(2)}`);
                }
                if (pts.length < 2) return;
                const pl = document.createElementNS(svg.namespaceURI, 'polyline');
                pl.setAttribute('points', pts.join(' '));
                pl.setAttribute('fill', 'none');
                pl.setAttribute('stroke', opts.stroke);
                pl.setAttribute('stroke-width', '1.5');
                pl.setAttribute('stroke-dasharray', opts.dashed ? '6 4' : '0');
                pl.setAttribute('stroke-linejoin', 'round');
                svg.appendChild(pl);

                if (opts.closed && opts.fill && pts.length >= 3) {
                    const pg = document.createElementNS(svg.namespaceURI, 'polygon');
                    pg.setAttribute('points', pts.join(' '));
                    pg.setAttribute('fill', opts.fill);
                    pg.setAttribute('stroke', 'none');
                    svg.insertBefore(pg, svg.firstChild);
                }
            };

            for (const rec of records) {
                const poly = worldVertsVec(rec);
                const closed = poly.length >= 3;
                if (!closed) continue;
                const sel = rec.id === selectedId;
                const hov = rec.id === hoverPolyId;
                const { stroke, fill } = colorsForPriority(rec.status, hov, sel);
                drawLoop(poly, {
                    dashed: false,
                    closed: true,
                    fill,
                    stroke,
                    hover: hov || sel,
                });
                if (sel && canEditVertices()) {
                    rec.vertices.forEach((t) => {
                        const v = new Vec3(t[0], t[1], t[2]);
                        projectToScreen(v, scrA);
                        if (!scrA.ok) return;
                        const rect = document.createElementNS(svg.namespaceURI, 'rect');
                        rect.setAttribute('x', String(scrA.x - 3));
                        rect.setAttribute('y', String(scrA.y - 3));
                        rect.setAttribute('width', '6');
                        rect.setAttribute('height', '6');
                        rect.setAttribute('fill', WHITE);
                        rect.setAttribute('stroke', stroke);
                        rect.setAttribute('stroke-width', '1');
                        svg.appendChild(rect);
                    });
                }
            }

            if (active && draft.length > 0) {
                const { stroke } = colorsForPriority('low', false, false);
                const strokePts = [...draft];
                if (cursorGhost) strokePts.push(cursorGhost);
                drawLoop(strokePts, { dashed: true, closed: false, fill: null, stroke: WHITE, hover: false });
                draft.forEach((v) => {
                    projectToScreen(v, scrA);
                    if (!scrA.ok) return;
                    const c = document.createElementNS(svg.namespaceURI, 'circle');
                    c.setAttribute('cx', String(scrA.x));
                    c.setAttribute('cy', String(scrA.y));
                    c.setAttribute('r', '4');
                    c.setAttribute('fill', WHITE);
                    c.setAttribute('stroke', stroke);
                    svg.appendChild(c);
                });
            }

            if (detailCardRecId) {
                positionDetailCard(detailCardRecId);
            }

            scene.forceRender = true;
        };

        const hitTestPolygonEdges = (nx: number, ny: number): string | null => {
            let best: { id: string; d: number } | null = null;
            for (const rec of records) {
                const poly = ensureClosedLoopWorld(worldVertsVec(rec));
                if (poly.length < 3) continue;
                const m = poly.length;
                for (let i = 0; i < m; i++) {
                    const a = poly[i];
                    const b = poly[(i + 1) % m];
                    projectToScreen(a, scrA);
                    projectToScreen(b, scrB);
                    if (!scrA.ok || !scrB.ok) continue;
                    const d = distPointSeg2D(nx, ny, scrA.x, scrA.y, scrB.x, scrB.y);
                    if ((!best || d < best.d) && d < HIT_PX) {
                        best = { id: rec.id, d };
                    }
                }
            }
            return best?.id ?? null;
        };

        const hitTestPolygonFill = (nx: number, ny: number): string | null => {
            for (let i = records.length - 1; i >= 0; i--) {
                const rec = records[i];
                const poly = ensureClosedLoopWorld(worldVertsVec(rec));
                if (poly.length < 3) continue;
                let inside = false;
                for (let a = 0, b = poly.length - 1; a < poly.length; b = a++) {
                    projectToScreen(poly[a], scrA);
                    projectToScreen(poly[b], scrB);
                    if (!scrA.ok || !scrB.ok) {
                        inside = false;
                        break;
                    }
                    const xi = scrA.x;
                    const yi = scrA.y;
                    const xj = scrB.x;
                    const yj = scrB.y;
                    const intersect = yi > ny !== yj > ny && nx < ((xj - xi) * (ny - yi)) / (yj - yi + 1e-12) + xi;
                    if (intersect) inside = !inside;
                }
                if (inside) return rec.id;
            }
            return null;
        };

        const hitTestPolygon = (nx: number, ny: number): string | null =>
            hitTestPolygonEdges(nx, ny) ?? hitTestPolygonFill(nx, ny);

        const hitTestVertex = (nx: number, ny: number): { id: string; idx: number } | null => {
            const rec = selectedRec();
            if (!rec || !canEditVertices()) return null;
            let best: { id: string; idx: number; d: number } | null = null;
            rec.vertices.forEach((t, idx) => {
                const v = new Vec3(t[0], t[1], t[2]);
                projectToScreen(v, scrA);
                if (!scrA.ok) return;
                const d = Math.hypot(nx - scrA.x, ny - scrA.y);
                if ((!best || d < best.d) && d < VERTEX_HIT_PX) {
                    best = { id: rec.id, idx, d };
                }
            });
            return best;
        };

        const hideDetailCard = () => {
            if (detailCardEl) {
                detailCardEl.remove();
                detailCardEl = null;
                detailCardRecId = null;
            }
        };

        const positionDetailCard = (recId: string) => {
            if (!detailCardEl || detailCardRecId !== recId) return;
            const rec = records.find((r) => r.id === recId);
            if (!rec) return;
            const hostRect = cardHost.getBoundingClientRect();
            if (hostRect.width === 0 || hostRect.height === 0) return;
            const cv = new Vec3(rec.position[0], rec.position[1], rec.position[2]);
            projectToScreen(cv, scrA);
            if (!scrA.ok) return;
            const gap = 12;
            let left = scrA.x + gap;
            let top = scrA.y;
            const cardW = detailCardEl.offsetWidth || 220;
            const cardH = detailCardEl.offsetHeight || 120;
            if (left + cardW > hostRect.width - 8) left = scrA.x - cardW - gap;
            if (left < 8) left = 8;
            if (top - cardH / 2 < 8) top = cardH / 2 + 8;
            if (top + cardH / 2 > hostRect.height - 8) top = hostRect.height - cardH / 2 - 8;
            detailCardEl.style.left = `${left}px`;
            detailCardEl.style.top = `${top}px`;
        };

        const showDetailCard = (recId: string) => {
            if (detailCardEl && detailCardRecId === recId) return;
            hideDetailCard();

            const rec = records.find((r) => r.id === recId);
            if (!rec) return;

            detailCardRecId = recId;
            const card = document.createElement('div');
            card.className = isEditMode
                ? 'annotation-hover-card annotation-hover-card--edit boundary-polygon-detail-card'
                : 'annotation-hover-card annotation-hover-card--view boundary-polygon-detail-card';

            const num = displayIndexForId(recId);
            const badge = document.createElement('span');
            badge.className = 'annotation-hover-number';
            badge.textContent = `${num}.`;

            if (isEditMode) {
                const input = document.createElement('input');
                input.type = 'text';
                input.maxLength = 50;
                input.className = 'annotation-hover-title-input';
                input.value = rec.title || '';
                input.placeholder = 'Polygon title';

                const descInput = document.createElement('textarea');
                descInput.maxLength = 300;
                descInput.setAttribute('maxlength', '300');
                descInput.className = 'annotation-hover-desc-input';
                descInput.value = rec.text || '';
                descInput.placeholder = 'Description (optional)';
                descInput.rows = 2;

                const persistDetail = (patch?: { title?: string; text?: string; status?: AnnotationPriority }) => {
                    const newTitle = patch?.title ?? input.value.trim();
                    const newText = patch?.text ?? descInput.value.slice(0, 300).trim();
                    const newStatus = patch?.status ?? rec.status;
                    if (newTitle === rec.title && newText === rec.text && newStatus === rec.status) return;
                    rec.title = newTitle || `Polygon ${num}`;
                    rec.text = newText;
                    rec.status = newStatus;
                    fireUpsert(rec);
                    paint();
                };

                const commitFields = () => persistDetail();

                card.addEventListener('focusout', (e: FocusEvent) => {
                    const next = e.relatedTarget as Node | null;
                    if (next && card.contains(next)) return;
                    commitFields();
                });

                input.addEventListener('keydown', (e: KeyboardEvent) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        commitFields();
                        descInput.focus();
                    }
                });

                const priorityRow = document.createElement('div');
                priorityRow.className = 'annotation-hover-priority-row';
                const priorityLabel = document.createElement('span');
                priorityLabel.className = 'annotation-hover-priority-label';
                priorityLabel.textContent = 'Priority';
                priorityRow.appendChild(priorityLabel);
                priorityRow.appendChild(
                    buildAnnotationPrioritySelect(normalizeAnnotationPriority(rec.status), (priority) => {
                        persistDetail({ status: priority });
                    }),
                );

                const closeBtn = document.createElement('button');
                closeBtn.type = 'button';
                closeBtn.className = 'annotation-hover-close';
                closeBtn.textContent = '\u00D7';
                closeBtn.title = 'Close panel';
                closeBtn.setAttribute('aria-label', 'Close polygon panel');
                closeBtn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    commitFields();
                    selectedId = null;
                    hideDetailCard();
                    paint();
                    events.fire('boundary.selectionChanged', null);
                });

                const deleteBtn = document.createElement('button');
                deleteBtn.type = 'button';
                deleteBtn.className = 'annotation-hover-delete';
                deleteBtn.title = 'Delete polygon';
                deleteBtn.setAttribute('aria-label', 'Delete polygon');
                deleteBtn.innerHTML =
                    '<svg class="annotation-hover-delete-icon" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true"><path d="M216,48H176V40a24,24,0,0,0-24-24H104A24,24,0,0,0,80,40v8H40a8,8,0,0,0,0,16h8V208a16,16,0,0,0,16,16H192a16,16,0,0,0,16-16V64h8a8,8,0,0,0,0-16ZM96,40a8,8,0,0,1,8-8h48a8,8,0,0,1,8,8v8H96Zm96,168H64V64H192ZM112,104v64a8,8,0,0,1-16,0V104a8,8,0,0,1,16,0Zm48,0v64a8,8,0,0,1-16,0V104a8,8,0,0,1,16,0Z"/></svg>';
                deleteBtn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    hideDetailCard();
                    deleteSelectedPolygon();
                });

                const actionsCol = document.createElement('div');
                actionsCol.className = 'annotation-hover-actions';
                actionsCol.appendChild(closeBtn);
                actionsCol.appendChild(deleteBtn);

                const headerRow = document.createElement('div');
                headerRow.className = 'annotation-hover-header';
                headerRow.appendChild(badge);
                headerRow.appendChild(input);

                card.appendChild(headerRow);
                card.appendChild(descInput);
                card.appendChild(priorityRow);
                card.appendChild(actionsCol);
            } else {
                const areaType = document.createElement('span');
                areaType.className = 'boundary-polygon-area-type';
                areaType.textContent = 'Area Selection';

                const titleLine = document.createElement('span');
                titleLine.className = 'boundary-polygon-title-line';
                titleLine.textContent = `${num}. ${rec.title || `Polygon ${num}`}`;

                card.appendChild(areaType);
                card.appendChild(titleLine);

                if (rec.text) {
                    const desc = document.createElement('span');
                    desc.className = 'annotation-hover-desc';
                    desc.textContent = rec.text;
                    card.appendChild(desc);
                }

                const priorityRow = buildAnnotationPriorityReadonly(normalizeAnnotationPriority(rec.status));
                if (priorityRow) card.appendChild(priorityRow);

                const closeBtn = document.createElement('button');
                closeBtn.type = 'button';
                closeBtn.className = 'annotation-hover-close boundary-polygon-detail-close';
                closeBtn.textContent = '\u00D7';
                closeBtn.title = 'Close';
                closeBtn.setAttribute('aria-label', 'Close polygon details');
                closeBtn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    selectedId = null;
                    hideDetailCard();
                    paint();
                    events.fire('boundary.selectionChanged', null);
                });
                card.appendChild(closeBtn);
            }

            ['pointerdown', 'pointerup', 'pointermove', 'click', 'dblclick', 'wheel'].forEach((name) => {
                card.addEventListener(name, (e) => e.stopPropagation());
            });

            cardHost.appendChild(card);
            detailCardEl = card;
            card.style.pointerEvents = 'auto';
            requestAnimationFrame(() => positionDetailCard(recId));
        };

        const closeDraft = () => {
            if (draft.length < 3) return;
            const verts: [number, number, number][] = draft.map((v) => [v.x, v.y, v.z]);
            const cv = centroidVectors(draft);
            const rec: BoundaryRecord = {
                id: genId(),
                kind: 'boundary',
                title: `Polygon ${records.length + 1}`,
                vertices: verts,
                position: [cv.x, cv.y, cv.z],
                volumeM3: null,
                areaM2: null,
                basePlane: null,
                computedAt: null,
                text: '',
                imageUrl: '',
                status: 'low',
                tags: [],
                cameraView: null,
            };
            records.push(rec);
            draft.length = 0;
            cursorGhost = null;
            selectedId = rec.id;
            recomputeVolume(rec);
            fireUpsert(rec);
            paint();
            events.fire('boundary.selectionChanged', rec.id);
            if (isEditMode && isIdleInteraction()) {
                showDetailCard(rec.id);
            }
        };

        const tryPickWorld = async (e: PointerEvent): Promise<Vec3 | null> => {
            const { nx, ny } = pointerPositionInElement(e, scene.canvas);
            const hit = await scene.camera.intersect(nx, ny);
            if (!hit?.position) return null;
            return hit.position.clone();
        };

        const onPointerMoveCanvas = (e: PointerEvent) => {
            clickGate = false;
            const host = canvasContainer.dom;
            const { x, y } = pointerPositionInElement(e, host);

            if (dragVertex) {
                const rec = records.find((r) => r.id === dragVertex!.id);
                if (rec) {
                    void tryPickWorld(e).then((p) => {
                        if (!p || !dragVertex) return;
                        rec.vertices[dragVertex.idx] = [p.x, p.y, p.z];
                        recalcCentroid(rec);
                        scheduleRecompute(rec);
                        paint();
                    });
                }
                return;
            }

            if (active && draft.length > 0) {
                void tryPickWorld(e).then((p) => {
                    cursorGhost = p;
                    paint();
                });
            }

            const idle = isIdleInteraction();
            const canHover = idle || (active && draft.length === 0);
            const prevHover = hoverPolyId;
            hoverPolyId = canHover ? hitTestPolygon(x, y) : null;
            if (prevHover !== hoverPolyId) paint();
        };

        const trySelectExisting = (e: PointerEvent, x: number, y: number, host: HTMLElement): boolean => {
            const vhit = hitTestVertex(x, y);
            if (vhit && e.button === 0) {
                dragVertex = vhit;
                selectedId = vhit.id;
                hideDetailCard();
                try {
                    host.setPointerCapture(e.pointerId);
                } catch {
                    /* ignore */
                }
                paint();
                events.fire('boundary.selectionChanged', vhit.id);
                return true;
            }
            const hid = hitTestPolygon(x, y);
            if (hid && e.button === 0) {
                selectedId = hid;
                paint();
                events.fire('boundary.selectionChanged', hid);
                pendingSelectOnly = true;
                clickGate = false;
                return true;
            }
            if (e.button === 0 && !hid) {
                selectedId = null;
                hideDetailCard();
                paint();
                events.fire('boundary.selectionChanged', null);
            }
            return false;
        };

        const onPointerDownCanvas = (e: PointerEvent) => {
            const isPrimary = e.pointerType === 'mouse' ? e.button === 0 : e.isPrimary;
            if (isPrimary) clickGate = true;
            pendingSelectOnly = false;
            const host = canvasContainer.dom;
            const { x, y } = pointerPositionInElement(e, host);

            if (!active && !isIdleInteraction()) return;

            if (!active) {
                if (trySelectExisting(e, x, y, host)) {
                    if (selectedId && pendingSelectOnly) {
                        if (selectedId === detailCardRecId) {
                            hideDetailCard();
                            selectedId = null;
                            events.fire('boundary.selectionChanged', null);
                        } else {
                            showDetailCard(selectedId);
                        }
                    }
                }
                return;
            }

            if (draft.length === 0 && trySelectExisting(e, x, y, host)) {
                return;
            }
        };

        const onPointerUpCanvas = (e: PointerEvent) => {
            if (dragVertex) {
                dragVertex = null;
                try {
                    canvasContainer.dom.releasePointerCapture(e.pointerId);
                } catch {
                    /* ignore */
                }
                const rec = selectedRec();
                if (rec) fireUpsert(rec);
                return;
            }

            const isPrimary = e.pointerType === 'mouse' ? e.button === 0 : e.isPrimary;
            if (!active || !clickGate || !isPrimary) return;
            if (pendingSelectOnly) {
                pendingSelectOnly = false;
                return;
            }
            clickGate = false;
            void tryPickWorld(e).then((p) => {
                if (!p) return;
                draft.push(p);
                cursorGhost = null;
                paint();
            });
        };

        const onDblClickCanvas = (e: PointerEvent) => {
            if (!active) return;
            e.preventDefault();
            e.stopPropagation();
            closeDraft();
        };

        const deleteSelectedPolygon = () => {
            const rec = selectedRec();
            if (!rec) return;
            const idx = records.findIndex((r) => r.id === rec.id);
            if (idx >= 0) records.splice(idx, 1);
            fireDeleteId(rec.id);
            selectedId = null;
            hideDetailCard();
            paint();
            events.fire('boundary.selectionChanged', null);
        };

        const onKeyDown = (e: KeyboardEvent) => {
            const t = e.target as HTMLElement;
            if (t?.closest?.('input, textarea, select, [contenteditable=true]')) return;

            if (e.key === 'Escape') {
                if (detailCardRecId) {
                    e.preventDefault();
                    hideDetailCard();
                    selectedId = null;
                    paint();
                    events.fire('boundary.selectionChanged', null);
                    return;
                }
            }

            if (active) {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    closeDraft();
                    return;
                }
                if (e.key === 'Escape') {
                    e.preventDefault();
                    if (draft.length > 0) {
                        draft.length = 0;
                        cursorGhost = null;
                        paint();
                    } else if (selectedId) {
                        selectedId = null;
                        hideDetailCard();
                        paint();
                        events.fire('boundary.selectionChanged', null);
                    }
                    return;
                }
                if ((e.key === 'Delete' || e.key === 'Backspace') && draft.length === 0 && selectedRec()) {
                    e.preventDefault();
                    deleteSelectedPolygon();
                }
                return;
            }

            if (e.key !== 'Delete' && e.key !== 'Backspace') return;
            if (!selectedRec() || !isEditMode) return;
            e.preventDefault();
            deleteSelectedPolygon();
        };

        events.on('postrender', paint);

        events.on('tool.activated', (toolName: string | null) => {
            if (toolName === 'boundaryPolygon') {
                hideDetailCard();
                return;
            }
            if (toolName) hideDetailCard();
        });

        events.on('volugraph.editModeChange', (editMode: boolean) => {
            isEditMode = editMode;
            hideDetailCard();
            selectedId = null;
            paint();
        });

        const volugraphRoot = canvasContainer.dom.closest?.('[data-volugraph-embed]') ?? document.body;
        isEditMode = volugraphRoot.getAttribute?.('data-edit-mode') === 'true';

        canvasContainer.dom.addEventListener('pointermove', onPointerMoveCanvas);
        canvasContainer.dom.addEventListener('pointerdown', onPointerDownCanvas);
        canvasContainer.dom.addEventListener('pointerup', onPointerUpCanvas, true);
        canvasContainer.dom.addEventListener('dblclick', onDblClickCanvas);
        window.addEventListener('keydown', onKeyDown);

        let markersGloballyVisible = true;
        const applyMarkersGlobalVisibility = () => {
            overlayHost.classList.toggle('boundary-polygon-overlay--hidden', !markersGloballyVisible);
            cardHost.classList.toggle('boundary-polygon-card-host--hidden', !markersGloballyVisible);
            if (!markersGloballyVisible) {
                hideDetailCard();
            } else {
                paint();
            }
        };
        events.on('annotation.setMarkersVisible', (visible: boolean) => {
            markersGloballyVisible = visible !== false;
            applyMarkersGlobalVisibility();
        });

        function importAnnotations(list: unknown) {
            if (!Array.isArray(list)) return;
            const prevSel = selectedId;
            const byId = new Map<string, BoundaryRecord>();
            for (const raw of list as Record<string, unknown>[]) {
                if (raw?.kind !== 'boundary') continue;
                const p = parseImportedBoundary(raw);
                if (p) byId.set(p.id, p);
            }
            records.length = 0;
            byId.forEach((v) => records.push(v));

            if (prevSel && records.some((r) => r.id === prevSel)) selectedId = prevSel;
            else selectedId = null;
            hideDetailCard();
            paint();
            applyMarkersGlobalVisibility();
            events.fire('boundary.changed');
        }

        events.on('volugraph.importAnnotations', (list: unknown) => {
            importAnnotations(list);
        });
        applyMarkersGlobalVisibility();

        events.removeFunction('boundary.export');
        events.removeFunction('boundary.list');
        events.removeFunction('boundary.hasAny');
        events.function('boundary.export', () => records.map((r) => ({ ...r })));
        events.function('boundary.list', () => records.map((r) => ({ ...r })));
        events.function('boundary.hasAny', () => records.length > 0);

        this.activate = () => {
            active = true;
            draft.length = 0;
            cursorGhost = null;
            hideDetailCard();
            parent.style.display = 'block';
            parent.classList.add('noevents');
            paint();
        };

        this.deactivate = () => {
            active = false;
            draft.length = 0;
            cursorGhost = null;
            parent.style.display = 'none';
            parent.classList.remove('noevents');
            paint();
        };

        paint();
    }
}

export { BoundaryPolygonTool };
